import type { BulkPackTakedownBody } from '@planuze/admin-contracts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkTakedownPackVersions,
  getPackDetail,
  listOrgPackages,
  listPublishedPacks,
  takedownPackVersion,
} from '../lib/packs-catalog-api.js';

/* Hook TanStack Query do catálogo de packs publicados (read-only, ADR-0400). Paginação por
   cursor keyset via useInfiniteQuery (espelha useCompaniesList): a page achata `data.pages`.
   `queryKey` prefixo `['packs-catalog']` p/ o takedown invalidar lista + detalhe + org juntos. */
export function usePublishedPacksList() {
  return useInfiniteQuery({
    queryKey: ['packs-catalog', 'list'],
    queryFn: ({ pageParam }) => listPublishedPacks({ cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/* Detalhe rico de UM pack (Fase 2, ADR-0703+): metadata + versões via endpoint dedicado. */
export function usePackDetail(packId: string) {
  return useQuery({
    queryKey: ['packs-catalog', 'detail', packId],
    queryFn: () => getPackDetail(packId),
  });
}

/* Takedown de versão por staff (ADR-0464): invalida o catálogo no sucesso (latestActive muda). */
export function useTakedownPackVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      readonly packId: string;
      readonly version: string;
      readonly reason: string;
    }) => takedownPackVersion(input.packId, input.version, input.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packs-catalog'] }).catch(() => undefined);
    },
  });
}

/** Takedown homogêneo por pares pack+versão congelados; sempre re-sincroniza o catálogo. */
export function useBulkTakedownPackVersions() {
  const queryClient = useQueryClient();
  const invalidateCatalog = (): void => {
    queryClient.invalidateQueries({ queryKey: ['packs-catalog'] }).catch(() => undefined);
  };
  return useMutation({
    mutationFn: (input: BulkPackTakedownBody) => bulkTakedownPackVersions(input),
    onSuccess: invalidateCatalog,
    onError: invalidateCatalog,
  });
}

/* Packages org-private de uma org (aba Companies, ADR-0400+, read-only). */
export function useOrgPackages(orgId: string) {
  return useQuery({
    queryKey: ['packs-catalog', 'org', orgId],
    queryFn: () => listOrgPackages(orgId),
  });
}
